import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { publicApi } from './api.ts';
import { brandTints, normaliseHex } from './brandTints.ts';
import {
  browserBrandingStorage,
  preloadBranding as preloadBrandingCore,
  type PreloadedBranding,
} from './brandingPreload.ts';
import type { Branding } from './types.ts';

// The minimal client shape BrandingProvider needs. publicApi/adminApi/opsApi all
// satisfy it. Authed apps (admin) pass their own client so the branding GET
// carries a bearer token — the API resolves the caller's tenant from it when no
// hostname tenant applies (the SPAs call the API cross-origin on the api host).
export type BrandingApi = { get<T>(path: string): Promise<T> };

type BrandingContextValue = {
  branding: Partial<Branding>;
  loading: boolean;
  reload: () => Promise<void>;
  /**
   * Paint a brand the caller already holds, with no request — e.g. the admin
   * Settings save, whose PUT /api/settings answers with the stored branding.
   * Calling reload() there was a second GET of what the PUT had just returned.
   * Supersedes any load or preload revalidation still in flight.
   */
  apply: (branding: Partial<Branding>) => void;
};

/**
 * The product's own colours, exported so Settings' "Reset to defaults" and its
 * preview read the same three values the app falls back to — rather than
 * becoming yet another copy of the hexes. Keep in step with PLATFORM_BRAND in
 * apps/api/src/lib/platformBrand.ts, which is what /api/settings actually serves.
 */
export const BRAND_COLOR_DEFAULTS = {
  primaryColor: '#1C5434',
  primaryDark: '#0E4026',
  accentColor: '#1C5434',
} as const;

const FALLBACK: Branding = {
  companyName: 'SupplierClear Safety & Compliance',
  tagline: 'Powered by SupplierClear',
  // Empty, not a path: SupplierClear's identity is the <BrandMark> lockup, drawn
  // as an inline mark rather than loaded as a file. An empty string selects the
  // lockup, where a path to a non-existent asset would render a broken image in
  // every tenant that has not uploaded a logo.
  //
  // This DEPENDS on every consumer branching `logoUrl ? <img/> : <BrandMark/>`.
  // An earlier version of this comment asserted they all did; five did not —
  // AdminAcceptInvite, ForgotPassword, ResetPassword, CompanyLanding and
  // DriverLanding used a bare `logoUrl &&` and so rendered NO logo at all once
  // this emptied. They now branch. Any new optional-logo site must do the same:
  // emptying this default turns `&&` into a silently missing mark, and no test
  // asserts a logo is visible.
  logoUrl: '',
  // SupplierClear green. HEX, not oklch — Settings.tsx validates these against
  // HEX_RE. Must stay in lockstep with PLATFORM_BRAND in the API, which wins.
  ...BRAND_COLOR_DEFAULTS,
  emailFromName: 'SupplierClear Safety & Compliance',
  // Display-only: no email is ever sent from this value (SENDER in
  // apps/api/src/lib/email.ts is the only SES envelope). It is what the
  // sign-in / expired / locked pages offer as the help address (via
  // supportEmailOf) for any tenant whose stored blob has no address of its
  // own, because the provider merges `{ ...FALLBACK, ...blob }` — so it must be
  // a mailbox someone reads, never a no-reply. Keep in step with
  // PLATFORM_BRAND.emailFromAddress in apps/api/src/lib/platformBrand.ts.
  // Deliberately no `supportEmail` here: the spread would then beat a tenant's
  // own emailFromAddress.
  emailFromAddress: 'compliance@supplierclear.com',
};

const BrandingContext = createContext<BrandingContextValue>({
  branding: FALLBACK,
  loading: true,
  reload: async () => {},
  apply: () => {},
});

function applyCssVars(branding: Partial<Branding>): void {
  const root = document.documentElement;

  // Each solid colour is VALIDATED AND NORMALISED, not just
  // defaulted-if-absent. `Branding`'s regex is enforced on the settings PUT, but
  // this also paints rows written by older code and by cell seeding, and an
  // unparseable value assigned to --brand-primary is an invalid declaration: the
  // control loses its fill entirely rather than degrading to the product colour.
  // A present-but-broken value is exactly the case `?? FALLBACK` does not catch.
  //
  // normaliseHex rather than a parseHex check, for two reasons this code got
  // wrong: the value may not be a STRING at all (branding is a cast of raw
  // JSONB, and parseHex throws on a null — which the provider's catch turns into
  // "discard the whole tenant's branding"), and parseHex accepts a hash-less
  // `fff` that is not a valid CSS colour once written into the variable.
  const solid = (value: unknown, fallback: string): string => normaliseHex(value) ?? fallback;

  const primary = solid(branding.primaryColor, FALLBACK.primaryColor);
  root.style.setProperty('--brand-primary', primary);
  root.style.setProperty('--brand-primary-dark', solid(branding.primaryDark, FALLBACK.primaryDark));
  root.style.setProperty('--brand-accent', solid(branding.accentColor, FALLBACK.accentColor));

  // The tint variables are DERIVED, not left to the stylesheet. Pushing only the
  // three solid colours meant a tenant on purple got purple buttons and whatever
  // tint index.css happened to declare — green, since the SupplierClear rebrand —
  // behind active nav rows, avatar circles and the mobile CTAs.
  //
  // A colour we cannot parse leaves the stylesheet's values in place rather than
  // clearing them: an unreadable brand should degrade to the product palette, not
  // to no tint at all.
  const tints = brandTints(primary);
  if (tints) {
    root.style.setProperty('--brand-primary-light', tints.light);
    root.style.setProperty('--brand-active-bg', tints.activeBg);
    root.style.setProperty('--brand-active-text', tints.activeText);
  } else {
    // Unreachable while `primary` is validated above, and kept anyway because
    // leaving these alone is NOT the same as leaving the stylesheet's values in
    // place. These are inline properties on documentElement: once any tenant has
    // been applied they persist, so a later apply that skipped this branch would
    // keep painting the PREVIOUS tenant's tint behind active rows and avatars.
    // Removing them is what actually hands control back to index.css.
    root.style.removeProperty('--brand-primary-light');
    root.style.removeProperty('--brand-active-bg');
    root.style.removeProperty('--brand-active-text');
  }

  if (branding.companyName) {
    document.title = branding.companyName;
  }
}

/**
 * Fetch the tenant's brand and paint its CSS variables BEFORE the first render.
 * Call it beside initSupabaseConfig() in main.tsx and hand the result to
 * <BrandingProvider preloaded>. Never rejects. See brandingPreload.ts.
 *
 * Token-less by construction (publicApi): it is the only writer of the
 * per-origin cache, which must never hold a bearer-resolved brand.
 */
export async function preloadBranding(): Promise<PreloadedBranding> {
  const preloaded = await preloadBrandingCore({
    api: publicApi,
    storage: browserBrandingStorage(),
  });
  if (preloaded.branding) applyCssVars({ ...FALLBACK, ...preloaded.branding });
  return preloaded;
}

export function BrandingProvider({
  children,
  api = publicApi,
  preloaded,
}: {
  children: ReactNode;
  api?: BrandingApi;
  /**
   * From preloadBranding(). When given, the provider starts from it — its CSS
   * variables are already painted — and skips its own mount fetch, which would
   * only repeat the preload's token-less request. Omitted (ops), the provider
   * fetches on mount as before.
   */
  preloaded?: PreloadedBranding;
}): ReactNode {
  const [branding, setBranding] = useState<Partial<Branding>>(() =>
    preloaded?.branding ? { ...FALLBACK, ...preloaded.branding } : FALLBACK,
  );
  const [loading, setLoading] = useState(!preloaded || preloaded.pending !== null);
  // Bumped by every load(). A preload revalidation that lands after a load()
  // has started is stale — typically the token-less answer arriving after the
  // admin's bearer-resolved reload — and must not overwrite it.
  const generation = useRef(0);

  const load = useMemo(
    () => async () => {
      const mine = ++generation.current;
      try {
        const res = await api.get<{ branding: Partial<Branding> }>('/api/settings');
        if (mine !== generation.current) return;
        const merged: Partial<Branding> = { ...FALLBACK, ...res.branding };
        setBranding(merged);
        applyCssVars(merged);
      } catch {
        // Keep whatever is painted. This used to reset to FALLBACK, which on a
        // transient failure repainted a tenant's workspace in the product
        // green — the same wrong-colour flash the preload exists to remove. If
        // nothing was ever loaded, what is painted already IS the fallback.
      } finally {
        if (mine === generation.current) setLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    if (!preloaded) {
      void load();
      return;
    }
    const pending = preloaded.pending;
    if (!pending) return;
    const startedAt = generation.current;
    void pending.then((fresh) => {
      if (startedAt !== generation.current) return;
      if (fresh) {
        const merged: Partial<Branding> = { ...FALLBACK, ...fresh };
        setBranding(merged);
        applyCssVars(merged);
      }
      setLoading(false);
    });
  }, [load, preloaded]);

  const apply = useMemo(
    () => (next: Partial<Branding>) => {
      // Bumped like load() does, so a load or preload revalidation that
      // started earlier cannot land afterwards and paint the older brand.
      ++generation.current;
      const merged: Partial<Branding> = { ...FALLBACK, ...next };
      setBranding(merged);
      applyCssVars(merged);
      setLoading(false);
    },
    [],
  );

  const value = useMemo<BrandingContextValue>(
    () => ({ branding, loading, reload: load, apply }),
    [branding, loading, load, apply],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}
